class CountdownBar extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode || 'fixed';
    this.onEnd = this.dataset.onEnd || 'hide';
    this.resetTime = this.dataset.resetTime || '00:00';
    this.units = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };
    this.clock = this.querySelector('[data-countdown-clock]');
    this.endedEl = this.querySelector('[data-countdown-ended]');
    this.showDays = this.dataset.showDays === 'true';

    // A timer reading four months does the opposite of its job: it tells the
    // shopper there is no hurry. Above this many hours the bar stays out of the
    // way and only appears once the deadline is genuinely close. 0 = always on.
    this.showWithinMs = (parseFloat(this.dataset.showWithinHours) || 0) * 3600000;

    // Empty means the date and time are read in each visitor's own clock, so a
    // sale "ending at midnight" ends three hours later in Los Angeles than in
    // New York. A zone name pins it to one real instant for everyone.
    this.timeZone = this.dataset.timezone || '';
    if (this.timeZone) {
      try {
        new Intl.DateTimeFormat('en-US', { timeZone: this.timeZone });
      } catch (error) {
        console.warn('[countdown] unknown time zone, falling back to local:', this.timeZone);
        this.timeZone = '';
      }
    }

    this.target = this.computeTarget();
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  // How far the chosen zone sits from UTC at a given instant. Derived from the
  // browser's own zone database, so daylight saving is already accounted for.
  zoneOffset(ts) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(ts));

    const read = (type) => parseInt(parts.find((part) => part.type === type).value, 10);
    // Some engines report midnight as hour 24 rather than 0.
    const hour = read('hour') % 24;

    return (
      Date.UTC(read('year'), read('month') - 1, read('day'), hour, read('minute'), read('second')) -
      ts
    );
  }

  // The wall-clock time the merchant typed, resolved to a real instant.
  // The offset is applied twice: the first pass can land on the wrong side of a
  // daylight-saving switch, and the second corrects for it.
  toEpoch(year, month, day, hour, minute) {
    if (!this.timeZone) return new Date(year, month - 1, day, hour, minute, 0).getTime();

    const naive = Date.UTC(year, month - 1, day, hour, minute, 0);
    let ts = naive - this.zoneOffset(naive);
    ts = naive - this.zoneOffset(ts);
    return ts;
  }

  // Today's calendar date as the chosen zone sees it, which is not necessarily
  // the visitor's date.
  todayParts() {
    if (!this.timeZone) {
      const now = new Date();
      return [now.getFullYear(), now.getMonth() + 1, now.getDate()];
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());

    const read = (type) => parseInt(parts.find((part) => part.type === type).value, 10);
    return [read('year'), read('month'), read('day')];
  }

  computeTarget() {
    if (this.mode === 'daily') {
      const [h, m] = this.resetTime.split(':').map((n) => parseInt(n, 10));
      const hour = h || 0;
      const minute = m || 0;

      const [year, month, day] = this.todayParts();
      let ts = this.toEpoch(year, month, day, hour, minute);

      if (ts <= Date.now()) {
        // Step a calendar day rather than adding 24 hours, so a daylight-saving
        // change does not drift the reset time by an hour.
        const next = new Date(Date.UTC(year, month - 1, day) + 86400000);
        ts = this.toEpoch(
          next.getUTCFullYear(),
          next.getUTCMonth() + 1,
          next.getUTCDate(),
          hour,
          minute
        );
      }

      return ts;
    }

    const date = (this.dataset.endDate || '').trim();
    const time = (this.dataset.endTime || '23:59').trim();

    const dateMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(date);
    const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(time);
    if (!dateMatch || !timeMatch) return null;

    const ts = this.toEpoch(
      Number(dateMatch[1]),
      Number(dateMatch[2]),
      Number(dateMatch[3]),
      Number(timeMatch[1]),
      Number(timeMatch[2])
    );

    return isNaN(ts) ? null : ts;
  }

  tick() {
    if (this.target === null) {
      this.finish();
      return;
    }

    let remaining = this.target - Date.now();

    if (remaining <= 0) {
      if (this.mode === 'daily') {
        this.target = this.computeTarget();
        remaining = this.target - Date.now();
      } else {
        this.finish();
        return;
      }
    }

    if (this.showWithinMs) {
      const close = remaining <= this.showWithinMs;
      if (this.hidden === close) this.hidden = !close;
      if (!close) return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    let days = Math.floor(totalSeconds / 86400);
    let hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // When the days unit is hidden, roll those hours into the hours figure
    // so the timer still reads correctly instead of silently losing a day.
    if (!this.showDays) {
      hours += days * 24;
      days = 0;
    }

    this.write('days', days);
    this.write('hours', hours);
    this.write('minutes', minutes);
    this.write('seconds', seconds);
  }

  write(unit, value) {
    const el = this.units[unit];
    if (!el) return;
    const padded = unit === 'days' ? String(value) : String(value).padStart(2, '0');
    if (el.textContent !== padded) el.textContent = padded;
  }

  finish() {
    clearInterval(this.timer);

    if (this.onEnd === 'hide') {
      this.hidden = true;
      this.style.display = 'none';
      return;
    }

    if (this.onEnd === 'message') {
      if (this.clock) this.clock.hidden = true;
      if (this.endedEl) this.endedEl.hidden = false;
      return;
    }

    // 'zero' — leave the clock on screen reading all zeros
    this.write('days', 0);
    this.write('hours', 0);
    this.write('minutes', 0);
    this.write('seconds', 0);
  }
}

if (!customElements.get('countdown-bar')) {
  customElements.define('countdown-bar', CountdownBar);
}
